/**
 * Public renderer for the Property Article module.
 *
 * Reads the article list from `/api/property-articles` and shows:
 *   - the most-recent article inline in full
 *   - the rest as clickable summary cards below
 *   - clicking a summary opens the full article in a modal
 *
 * Each item is timestamped with an absolute + relative "X ago" label.
 *
 * The module's own admin config (theme, scheduler, "Generate Now" button)
 * lives in `ModuleLibrary.jsx` — this component is the public view only.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { getApiUrl } from "@/utils/api";
import { X, Sparkles, Clock, Share2 } from "lucide-react";
import { useLockBodyScroll } from "@/hooks/useLockBodyScroll";

const API = getApiUrl();

function extractCover(article) {
  if (article?.cover_image) return article.cover_image;
  // Fallback: pluck the first <img src="…"> from the content HTML.
  const html = article?.content_html || "";
  const m = html.match(/<img[^>]+src="([^"]+)"/i);
  return m ? m[1] : "";
}

function formatWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const abs = d.toLocaleString("en-GB", {
      dateStyle: "medium",
      timeStyle: "short",
    });
    const diff = (Date.now() - d.getTime()) / 1000;
    let rel;
    if (diff < 60) rel = "just now";
    else if (diff < 3600) rel = `${Math.floor(diff / 60)}m ago`;
    else if (diff < 86400) rel = `${Math.floor(diff / 3600)}h ago`;
    else rel = `${Math.floor(diff / 86400)}d ago`;
    return `${abs} · ${rel}`;
  } catch {
    return iso;
  }
}

function ArticleShareBar({ article }) {
  if (!article) return null;
  const url = typeof window !== "undefined"
    ? `${window.location.origin}${window.location.pathname}#article-${article.article_id}`
    : "";
  const title = article.title || "Property Insight";
  const encUrl = encodeURIComponent(url);
  const encText = encodeURIComponent(title);
  const wa = `https://api.whatsapp.com/send?text=${encText}%20${encUrl}`;
  const fb = `https://www.facebook.com/sharer/sharer.php?u=${encUrl}`;
  const tw = `https://twitter.com/intent/tweet?url=${encUrl}&text=${encText}`;
  return (
    <div className="flex items-center gap-2 mt-6 pt-4 border-t border-border" data-testid="article-share-bar">
      <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
        <Share2 className="h-3.5 w-3.5" /> Share this article
      </span>
      <a
        href={wa}
        target="_blank"
        rel="noopener noreferrer"
        className="text-xs px-2.5 py-1 rounded border border-border hover:bg-[#25D366]/10 hover:border-[#25D366]/50 text-[#25D366] font-medium"
        data-testid={`article-share-whatsapp-${article.article_id}`}
      >
        WhatsApp
      </a>
      <a
        href={fb}
        target="_blank"
        rel="noopener noreferrer"
        className="text-xs px-2.5 py-1 rounded border border-border hover:bg-[#1877F2]/10 hover:border-[#1877F2]/50 text-[#1877F2] font-medium"
        data-testid={`article-share-facebook-${article.article_id}`}
      >
        Facebook
      </a>
      <a
        href={tw}
        target="_blank"
        rel="noopener noreferrer"
        className="text-xs px-2.5 py-1 rounded border border-border hover:bg-black/5 font-medium"
        data-testid={`article-share-twitter-${article.article_id}`}
      >
        Twitter / X
      </a>
    </div>
  );
}

export default function PropertyArticleModule({ config = {} }) {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalArticle, setModalArticle] = useState(null);
  useLockBodyScroll(!!modalArticle);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/property-articles`, {
          params: { limit: 20 },
        });
        if (cancelled) return;
        setArticles(data?.items || []);
      } catch {
        if (cancelled) return;
        setArticles([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const heading = config.heading || "Property Insights";
  const subheading = config.subheading || "AI-curated market takes, grounded in the listings we're seeing right now.";

  // Article title style overrides. Every key is optional — if unset we fall
  // through to Site Styles. Applied via CSS variables on the article
  // container so the scoped stylesheet below picks them up on the
  // dangerously-injected <h1>.
  const titleVars = {
    "--article-title-font": config.title_font_family || "var(--font-heading, inherit)",
    "--article-title-size": config.title_font_size ? `${config.title_font_size}px` : "",
    "--article-title-weight": config.title_font_weight || "",
    "--article-title-align": config.title_align || "",
    "--article-title-color": config.title_color || "var(--heading-color, inherit)",
    "--article-title-transform": config.title_transform || "none",
  };

  // Module hero heading style overrides (pill + subheading + timestamp at
  // the top of the module). Applied as an inline style object because
  // these elements are rendered by us — no dangerouslySetInnerHTML.
  const headerStyle = {
    ...(config.heading_align ? { textAlign: config.heading_align } : {}),
  };
  const headingLabelStyle = {
    ...(config.heading_font_family ? { fontFamily: config.heading_font_family } : {}),
    ...(config.heading_font_size ? { fontSize: `${config.heading_font_size}px` } : {}),
    ...(config.heading_font_weight ? { fontWeight: Number(config.heading_font_weight) } : {}),
    ...(config.heading_color ? { color: config.heading_color } : {}),
    textTransform: config.heading_transform || "uppercase",
  };
  const subheadingCustomStyle = {
    fontFamily: config.heading_font_family || "var(--font-body, inherit)",
    ...(config.heading_color ? { color: config.heading_color } : {}),
    ...(config.subheading_font_size ? { fontSize: `${config.subheading_font_size}px` } : {}),
    ...(config.subheading_align ? { textAlign: config.subheading_align } : {}),
  };

  // Site Styles hooks — every text element pulls fonts + colours from the
  // CSS variables set by `useSiteStyles`, so this module always matches the
  // rest of the site without hard-coding brand colours.
  const headingStyle = {
    fontFamily: "var(--font-heading, inherit)",
    color: "var(--heading-color, inherit)",
  };
  const bodyStyle = {
    fontFamily: "var(--font-body, inherit)",
    color: "var(--body-color, inherit)",
  };
  const proseStyle = {
    ...bodyStyle,
    // Prose plugin's own headings + captions inherit from Site Styles.
    "--tw-prose-headings": "var(--heading-color, inherit)",
    "--tw-prose-body": "var(--body-color, var(--tw-prose-body))",
    "--tw-prose-captions": "var(--body-color, var(--tw-prose-captions))",
    // Links: fall back to the Tailwind primary token (hsl(var(--primary))).
    "--tw-prose-links": "hsl(var(--primary))",
  };
  // Prose plugin doesn't accept a CSS var for heading font/size — we hand
  // the computed values in via a scoped inline stylesheet so H1–H6 inside
  // the article render using the master admin's Site Styles heading sizes
  // (--h1-size.. --h6-size) and weight/line-height, instead of Tailwind
  // Typography's own fixed prose scale. `!important` + later source order
  // is required to beat the `prose-h1:text-4xl` utility classes on the
  // <article> below. Per-instance title overrides (set further down) are
  // appended after, so they still win when the admin configures them —
  // but are clamped with CSS max() so a stale/small override (e.g. a
  // legacy value from before Site Styles existed) can never render H1
  // smaller than the site's own H2 size, which would invert the hierarchy.
  const proseFontOverride = `
    .article-prose h1, .article-prose h2, .article-prose h3,
    .article-prose h4, .article-prose h5, .article-prose h6 {
      font-weight: var(--heading-weight, 700) !important;
      line-height: var(--heading-line-height, 1.2) !important;
    }
    .article-prose h1 { font-size: var(--h1-size, 48px) !important; }
    .article-prose h2 { font-size: var(--h2-size, 36px) !important; }
    .article-prose h3 { font-size: var(--h3-size, 24px) !important; }
    .article-prose h4 { font-size: var(--h4-size, 20px) !important; }
    .article-prose h5 { font-size: var(--h5-size, 18px) !important; }
    .article-prose h6 { font-size: var(--h6-size, 16px) !important; }
    .article-prose h1 {
      font-family: var(--article-title-font, var(--font-heading, inherit));
      color: var(--article-title-color, var(--heading-color, inherit));
      text-transform: var(--article-title-transform, none);
    }
    ${config.title_font_size ? `.article-prose h1 { font-size: max(${Number(config.title_font_size)}px, var(--h2-size, 36px)) !important; line-height: 1.1 !important; }` : ""}
    ${config.title_font_weight ? `.article-prose h1 { font-weight: ${Number(config.title_font_weight)} !important; }` : ""}
    ${config.title_align ? `.article-prose h1 { text-align: ${config.title_align} !important; }` : ""}
    .article-prose h2, .article-prose h3,
    .article-prose h4, .article-prose h5, .article-prose h6 {
      font-family: var(--font-heading, inherit);
      color: var(--heading-color, inherit);
    }
    .article-prose p, .article-prose li, .article-prose figcaption,
    .article-prose strong, .article-prose em {
      font-family: var(--font-body, inherit);
    }
  `;

  if (loading) {
    return (
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto text-center text-sm text-muted-foreground">
          Loading articles…
        </div>
      </section>
    );
  }

  if (articles.length === 0) {
    return (
      <section className="py-12 px-4" data-testid="property-article-module-empty">
        <div className="max-w-4xl mx-auto text-center space-y-2">
          <h2 style={headingStyle} className="text-3xl">{heading}</h2>
          <p className="text-muted-foreground text-sm" style={bodyStyle}>
            No articles yet. Admins can generate the first one from the CMS.
          </p>
        </div>
      </section>
    );
  }

  const [latest, ...rest] = articles;

  return (
    <section className="py-12 px-4" data-testid="property-article-module">
      {/* Scoped CSS so prose headings pick up the Site Styles heading font. */}
      <style>{proseFontOverride}</style>
      <div className="max-w-4xl mx-auto space-y-10">
        {/* Renders as <div>, NOT <header>, on purpose. The global Site Styles
            stylesheet (`useSiteStyles`) paints every `[data-public-site] header`
            with the site's header bg/text colour so the top-of-page
            HeaderModule matches the brand — but that selector would also
            hijack this module's article intro band and ignore the
            per-instance `heading_*` overrides the admin sets here. Using a
            plain <div> keeps the block semantically neutral and lets the
            per-instance style config drive the look. */}
        <div
          className="article-module-header space-y-3"
          style={{ textAlign: config.heading_align || "center", ...headerStyle }}
        >
          <div
            className="inline-flex items-center gap-2 text-xs tracking-widest opacity-90"
            style={headingLabelStyle}
            data-testid="property-article-module-heading"
          >
            <Sparkles className="h-3.5 w-3.5" />
            <span>{heading}</span>
          </div>
          {/* Hero header text INHERITS colour from the parent section so it
              stays legible whether the admin sets a light or dark background.
              We deliberately don't force `--body-color` here. */}
          <p className="text-sm max-w-2xl mx-auto opacity-90" style={subheadingCustomStyle}>
            {subheading}
          </p>
          <p
            className="inline-flex items-center gap-1 text-xs opacity-75"
            data-testid={`article-timestamp-${latest.article_id}`}
          >
            <Clock className="h-3 w-3" />
            {formatWhen(latest.generated_at)}
          </p>
        </div>

        <article
          className="article-prose prose max-w-none prose-h1:mt-0 prose-h1:mb-6 prose-img:rounded-md prose-img:my-6 prose-img:w-full prose-figcaption:text-center"
          style={{ ...proseStyle, ...titleVars }}
          data-testid="article-latest-content"
          dangerouslySetInnerHTML={{ __html: latest.content_html }}
        />
        <ArticleShareBar article={latest} />

        {rest.length > 0 && (
          <div className="pt-8 border-t border-border space-y-4">
            <h3 className="text-2xl" style={headingStyle}>More articles</h3>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="article-summary-list">
              {rest.map((a) => {
                const thumb = extractCover(a);
                return (
                <li
                  key={a.article_id}
                  className="border border-border rounded-lg overflow-hidden hover:shadow-md transition-shadow cursor-pointer bg-background flex"
                  onClick={() => setModalArticle(a)}
                  data-testid={`article-summary-${a.article_id}`}
                >
                  {thumb ? (
                    <img
                      src={thumb}
                      alt=""
                      className="w-28 h-28 object-cover flex-shrink-0"
                      loading="lazy"
                      data-testid={`article-thumb-${a.article_id}`}
                    />
                  ) : (
                    <div
                      className="w-28 h-28 bg-muted flex items-center justify-center flex-shrink-0"
                      aria-hidden="true"
                    >
                      <Sparkles className="h-6 w-6 text-muted-foreground/50" />
                    </div>
                  )}
                  <div className="p-3 flex-1 min-w-0">
                    <div className="text-xs text-muted-foreground mb-1">
                      <Clock className="inline h-3 w-3 mr-1" />
                      {formatWhen(a.generated_at)}
                    </div>
                    <div className="font-medium mb-1 line-clamp-2" style={headingStyle}>{a.title}</div>
                    <div className="text-sm text-muted-foreground line-clamp-2" style={bodyStyle}>{a.summary}</div>
                    <div className="mt-2 text-xs font-medium text-primary">Read article →</div>
                  </div>
                </li>
              );
              })}
            </ul>
          </div>
        )}
      </div>

      {modalArticle && (
        <div
          className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm overflow-y-auto"
          onClick={(e) => { if (e.target === e.currentTarget) setModalArticle(null); }}
          data-testid="article-modal-backdrop"
        >
          <div
            className="relative mx-auto my-8 w-[92vw] max-w-3xl bg-background rounded-md shadow-2xl p-8"
            role="dialog"
            aria-modal="true"
            data-testid="article-modal"
          >
            <button
              type="button"
              onClick={() => setModalArticle(null)}
              className="absolute top-3 right-3 h-9 w-9 rounded-full bg-background border border-border hover:bg-muted transition-colors inline-flex items-center justify-center"
              aria-label="Close article"
              data-testid="article-modal-close"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="mb-4">
              <p className="text-xs text-muted-foreground flex items-center gap-1 mb-2">
                <Clock className="h-3 w-3" /> {formatWhen(modalArticle.generated_at)}
              </p>
              {/* No separate <h2> title here — content_html already opens
                  with its own <h1>{title}</h1>, same as BlogArticlePage. */}
            </div>
            <article
              className="article-prose prose max-w-none prose-h1:mt-0 prose-h1:mb-6 prose-img:rounded-md prose-img:my-6 prose-img:w-full prose-figcaption:text-center"
              style={{ ...proseStyle, ...titleVars }}
              dangerouslySetInnerHTML={{ __html: modalArticle.content_html }}
            />
            <ArticleShareBar article={modalArticle} />
          </div>
        </div>
      )}
    </section>
  );
}
